import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { OptionsList as View } from '@views';
import { routes } from '@constants';
import { LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import { getAllOptionsExceptCurrentSizeOptions, isPriceValid, formatPrice } from '@helpers/itemEdit';
import { isClientError } from '@helpers/errorHandling';
import {
  applyOptionToSize, createValue, editValue, deleteValue,
} from '@store/menu/duck';

const OptionsList = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    sizes, options, items, optionValues, applyOptionToSizePhase, applyOptionToSizeError,
    createValuePhase, editValuePhase, deleteValuePhase,
  } = useSelector(state => state.menu);

  const sizeId = location.state?.sizeId;

  const initialOptionsValue = useMemo(
    () => getAllOptionsExceptCurrentSizeOptions(options, sizes, items, optionValues, sizeId),
    [options, sizes, items, optionValues, sizeId],
  );

  const [error, setError] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState(initialOptionsValue);
  const [expandedOptionId, setExpandedOptionId] = useState(null);

  const [valueId, setValueId] = useState(null);
  const [isValueModalOpen, setIsValueModalOpen] = useState(false);
  const [valueName, setValueName] = useState('');
  const [valuePrice, setValuePrice] = useState(0);
  const [isValuePreselected, setIsValuePreselected] = useState(false);
  const [valueImage, setValueImage] = useState('');
  const [valueImageFile, setValueImageFile] = useState(null);
  const [isValueDeleteModalOpen, setIsValueDeleteModalOpen] = useState(false);

  const isValueDataTheSame = (!!valueId && !!optionValues[valueId]
    && valueName === optionValues[valueId].name
    && parseInt(valuePrice * 100, 10) === optionValues[valueId].price
    && isValuePreselected === optionValues[valueId].preSelected
    && valueImage === optionValues[valueId].image
  );

  const handleValueModalClose = () => {
    setIsValueModalOpen(false);
    setValueId(null);
    setValueName('');
    setValuePrice(0);
    setIsValuePreselected(false);
    setValueImage('');
  };

  useEffect(() => {
    if (!location.state || !location.state.sizeId) {
      history.goBack();
    }
  }, []);

  useEffect(() => {
    if (applyOptionToSizePhase === SUCCESS) {
      history.goBack();
    }
    if (applyOptionToSizePhase === FAILURE) {
      if (isClientError(applyOptionToSizeError && applyOptionToSizeError.data.status)) { // Case when we get 4** error
        setError(applyOptionToSizeError.data.error);
      }
    }
  }, [applyOptionToSizePhase]);

  useEffect(() => {
    if (deleteValuePhase === SUCCESS
      || createValuePhase === SUCCESS
      || editValuePhase === SUCCESS) {
      handleValueModalClose();
      setFilteredOptions(getAllOptionsExceptCurrentSizeOptions(options, sizes, items, optionValues, sizeId));
      setIsValueDeleteModalOpen(false);
    }
    if (deleteValuePhase === FAILURE
      || createValuePhase === FAILURE
      || editValuePhase === FAILURE) {
      setIsValueModalOpen(false);
    }
  }, [deleteValuePhase, editValuePhase, createValuePhase]);

  const handleToastClose = () => {
    setError(null);
  };

  const onBackUpClick = () => {
    history.goBack();
  };

  const handleCreateNewOption = () => {
    history.push({
      pathname: routes.optionEdit,
      state: {
        sizeId,
        itemId: location.state?.itemId,
        categoryId: location.state?.categoryId,
      },
    });
  };

  const handleOptionApply = optionId => () => {
    dispatch(applyOptionToSize({ sizeId, optionId }));
  };

  const handleCreateNewOptionValue = () => {
    setIsValueModalOpen(true);
  };

  const handleOptionValueClick = selectedValueId => () => {
    const initialValueName = selectedValueId && optionValues[selectedValueId]
      ? optionValues[selectedValueId].name
      : '';
    const initialValuePrice = selectedValueId && optionValues[selectedValueId]
      ? optionValues[selectedValueId].price / 100
      : 0;
    const initialValuePreselectedState = selectedValueId && optionValues[selectedValueId]
      ? optionValues[selectedValueId].preSelected
      : false;
    const initialValueImage = selectedValueId && optionValues[selectedValueId]
      ? optionValues[selectedValueId].image
      : '';

    setValueId(selectedValueId);
    setValueName(initialValueName);
    setValuePrice(initialValuePrice);
    setIsValuePreselected(initialValuePreselectedState);
    setValueImage(initialValueImage);
    setIsValueModalOpen(true);
  };

  const handleSearchResult = filteredOptionIds => {
    setFilteredOptions(initialOptionsValue.filter(option => filteredOptionIds.includes(option.id)));
  };

  const expandOptionValues = optionId => () => {
    // close expanded option on second tap on it
    if (expandedOptionId === optionId) {
      setExpandedOptionId(null);
    } else {
      setExpandedOptionId(optionId);
    }
  };

  const optionsForSearch = useMemo(
    () => initialOptionsValue.map(option => ({
      id: option.id,
      name: option.name,
    })),
    [initialOptionsValue],
  );

  // OPTION VALUE
  const handleValueNameChange = e => {
    setValueName(e.target.value);
  };

  const handleValuePreselectedStateChange = () => {
    setIsValuePreselected(prevState => !prevState);
  };

  const handleValuePriceChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // if price === 0 and entered price !== 0 then we are showing entered value
      setValuePrice(formatPrice(parseInt(cash, 10) !== 0 && parseInt(valuePrice, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleValueImageChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      setValueImage(URL.createObjectURL(e.target.files[0]));
      setValueImageFile(e.target.files[0]);
    }
  };

  const handleValueEditSubmit = () => {
    if (isValueDataTheSame) {
      setIsValueModalOpen(false);
    } else {
      const valueData = new FormData();

      if ((!valueId && valueImage) || (valueId && valueImage !== optionValues[valueId].image)) {
        valueData.append('image', valueImageFile);
      }

      if (!valueId || (valueId && valueName !== optionValues[valueId].name)) {
        valueData.append('name', valueName);
      }

      if (!valueId || (valueId && isValuePreselected !== optionValues[valueId].preSelected)) {
        valueData.append('isPreSelected', isValuePreselected);
      }

      if (!valueId || (valueId && parseInt(valuePrice * 100, 10) !== optionValues[valueId].price)) {
        valueData.append('price', parseInt(valuePrice * 100, 10)); // Convert dolars to cents
      }

      if (valueId) {
        valueData.append('id', valueId);

        dispatch(editValue(
          valueData,
        ));
      } else {
        valueData.append('optionId', expandedOptionId);

        dispatch(createValue(
          valueData,
        ));
      }
    }
  };

  const handleValueDeleteModalChangeState = () => {
    setIsValueDeleteModalOpen(prevState => !prevState);
  };

  const handleValueDelete = () => {
    dispatch(deleteValue({
      id: parseInt(valueId, 10),
    }));
  };

  const isLoading = applyOptionToSizePhase === LOADING
  || createValuePhase === LOADING
  || editValuePhase === LOADING
  || deleteValuePhase === LOADING;

  return (
    <View
      isValueDeleteModalOpen={isValueDeleteModalOpen}
      onValueDeleteModalChangeState={handleValueDeleteModalChangeState}
      onValueDelete={handleValueDelete}
      isValueEditing={!!valueId}
      valueName={valueName}
      valueImage={valueImage}
      valuePrice={valuePrice.toString()}
      isValuePreselected={isValuePreselected}
      onValueModalClose={handleValueModalClose}
      isValueModalOpen={isValueModalOpen}
      onValueEditSubmit={handleValueEditSubmit}
      onValueImageChange={handleValueImageChange}
      onValuePriceChange={handleValuePriceChange}
      onValuePreselectedStateChange={handleValuePreselectedStateChange}
      onValueNameChange={handleValueNameChange}
      optionList={filteredOptions}
      optionsForSearch={optionsForSearch}
      expandedOptionId={expandedOptionId}
      error={error}
      isLoading={isLoading}
      onBackUpClick={onBackUpClick}
      onOptionClick={handleOptionApply}
      onOptionValueClick={handleOptionValueClick}
      onCreateOptionClick={handleCreateNewOption}
      onCreateOptionValueClick={handleCreateNewOptionValue}
      onToastClose={handleToastClose}
      onSearch={handleSearchResult}
      onDropDownClick={expandOptionValues}
    />
  );
};

export default OptionsList;
